import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileText, Gauge, KanbanSquare, Target } from "lucide-react";
import { defineModuleUi, from, unwrap, useOrg, usePeople } from "@vixio/data";
import { formatDate, formatInt, formatMoney, todayIso, addDaysIso, lazyPage } from "@vixio/ui";
import { opportunityTitle, STAGE_LABEL, type Opportunity } from "./api";
import { WeeklyPage, useDueFollowups, useWeekly } from "./WeeklyPage";

export * from "./api";

// Telas carregadas sob demanda: cada uma vira um arquivo à parte no build.
const PipelinePage = lazyPage(() => import("./PipelinePage"), "PipelinePage");
const OpportunityPage = lazyPage(() => import("./OpportunityPage"), "OpportunityPage");
const OpportunitiesPage = lazyPage(() => import("./ListPages"), "OpportunitiesPage");
const ProposalsPage = lazyPage(() => import("./ListPages"), "ProposalsPage");
const ProposalPage = lazyPage(() => import("./ProposalPage"), "ProposalPage");
const PublicProposalPage = lazyPage(() => import("./PublicProposalPage"), "PublicProposalPage");

function NextActionsWidget() {
  const org = useOrg();
  const until = addDaysIso(todayIso(), 7);
  const q = useQuery({
    queryKey: ["sales", "opportunities", "next-actions", org.id],
    queryFn: () =>
      unwrap<Opportunity[]>(
        from("sales", "v_opportunities")
          .select("*")
          .eq("org_id", org.id)
          .not("stage", "in", "(won,lost)")
          .not("next_action_on", "is", null)
          .lte("next_action_on", until)
          .order("next_action_on")
          .limit(8),
      ),
  });
  const people = usePeople(org.id, (q.data ?? []).map((o) => o.person_id));
  const today = todayIso();
  if (!q.data?.length) return <p className="muted">Nenhuma próxima ação nos próximos 7 dias.</p>;
  return (
    <ul className="vx-stack" style={{ gap: 8, margin: 0, padding: 0, listStyle: "none" }}>
      {q.data.map((o) => (
        <li key={o.id} className="vx-row" style={{ flexWrap: "nowrap", alignItems: "baseline" }}>
          <span className={o.next_action_on! < today ? "num text-crit" : "num text-2"} style={{ width: 44, flexShrink: 0 }}>
            {formatDate(o.next_action_on).slice(0, 5)}
          </span>
          <span style={{ minWidth: 0 }}>
            <Link to={`/comercial/oportunidades/${o.id}`}>{opportunityTitle(o, o.person_id ? people.data?.get(o.person_id)?.full_name : null)}</Link>
            <span className="text-2"> · {o.next_action}</span>
            <span className="muted"> ({STAGE_LABEL[o.stage]})</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

function WeekWidget() {
  const org = useOrg();
  const w = useWeekly(org.id).data?.[0];
  const due = useDueFollowups(org.id).data ?? [];
  return (
    <dl className="vx-dl">
      <dt>Conversas na semana</dt>
      <dd className="num">{formatInt(w?.conversas_qualificadas ?? 0)}</dd>
      <dt>Propostas enviadas</dt>
      <dd className="num">{formatInt(w?.propostas_enviadas ?? 0)}</dd>
      <dt>Em negociação</dt>
      <dd className="num">{formatMoney(w?.valor_em_negociacao_cents ?? 0)}</dd>
      <dt>Acompanhamentos vencidos</dt>
      <dd>
        {due.length ? <Link to="/comercial/semana#followups">{formatInt(due.length)} para fazer</Link> : <span className="num">0</span>}
      </dd>
    </dl>
  );
}

export default defineModuleUi({
  key: "sales",
  title: "Comercial",
  domain: "commercial",
  icon: KanbanSquare,
  menu: [
    { path: "/comercial/pipeline", label: "Pipeline", minRole: "member", icon: KanbanSquare, order: 10, keywords: "kanban etapas funil" },
    { path: "/comercial/oportunidades", label: "Oportunidades", minRole: "member", icon: Target, order: 20, keywords: "leads próxima ação" },
    { path: "/comercial/propostas", label: "Propostas", minRole: "member", icon: FileText, order: 30, keywords: "aceite link três opções piso" },
    { path: "/comercial/semana", label: "Painel semanal", minRole: "member", icon: Gauge, order: 50, keywords: "métricas conversão acompanhamento" },
  ],
  routes: [
    { path: "/comercial/pipeline", element: <PipelinePage />, minRole: "member" },
    { path: "/comercial/oportunidades", element: <OpportunitiesPage />, minRole: "member" },
    { path: "/comercial/oportunidades/:id", element: <OpportunityPage />, minRole: "member" },
    { path: "/comercial/propostas", element: <ProposalsPage />, minRole: "member" },
    { path: "/comercial/propostas/:id", element: <ProposalPage />, minRole: "member" },
    { path: "/comercial/semana", element: <WeeklyPage />, minRole: "member" },
  ],
  publicRoutes: [{ path: "/p/:token", element: <PublicProposalPage /> }],
  widgets: [
    { key: "sales.next", title: "Próximas ações (7 dias)", minRole: "member", span: 2, order: 10, element: NextActionsWidget },
    { key: "sales.week", title: "Semana comercial", minRole: "member", span: 1, order: 11, element: WeekWidget },
  ],
});

